import { Create, Delete, EndpointActionResponse, Update } from 'hammock';
import { ENDPOINTS } from 'site-constants';

import { ContentId, ContentMetadata, ContentType } from '@candu-ai/content/types';
import { setForceSnapshotCookie } from '@candu-ai/extension/journey';
import {
  ContentPlacement,
  LogicOptions,
  ModalTimeFrames,
  PAGE_OPTIONS_TXT,
  PageOptions,
  SelectorLocation,
  URLOptions,
} from '@candu-ai/placement/types';
import { Snapshot } from '@candu-ai/snapshots/types';
import { getLatestSnapshot } from '@candu-ai/snapshots/utils';
import { isTooltipContent } from 'craft/constants';
import { getSelectorFromIframe } from 'craft/utils/snapshot-iframe';

export const getDefaultPlacement = (
  contentMetadata: ContentMetadata,
  snapshotURL?: string,
): ContentPlacement => {
  if (contentMetadata.contentType === ContentType.Inline) {
    return {
      contentId: contentMetadata.id,
      id: '',
      type: contentMetadata.contentType,
      slug: contentMetadata.slug,
      selector: '',
      location: SelectorLocation.START,
      urls: [{ operation: { type: URLOptions.EXACTLY, operand: snapshotURL || '' }, not: false }],
      urlsLogicOperator: LogicOptions.ANY,
    };
  }

  if (contentMetadata.contentType === ContentType.HotspotGroup) {
    return {
      contentId: contentMetadata.id,
      id: '',
      type: contentMetadata.contentType,
      slug: contentMetadata.slug,
      urls: [{ operation: { type: URLOptions.EXACTLY, operand: snapshotURL || '' }, not: false }],
      urlsLogicOperator: LogicOptions.ANY,
    };
  }

  if (contentMetadata.contentType === ContentType.Tour) {
    return {
      contentId: contentMetadata.id,
      id: '',
      type: contentMetadata.contentType,
      slug: contentMetadata.slug,
      urls: [{ operation: { type: URLOptions.EXACTLY, operand: snapshotURL || '' }, not: false }],
      urlsLogicOperator: LogicOptions.ANY,
      showTimeframe: ModalTimeFrames.UNTIL_DISMISSED,
    };
  }
  return {
    contentId: contentMetadata.id,
    id: '',
    type: contentMetadata.contentType,
    slug: contentMetadata.slug,
    urls: [{ operation: { type: URLOptions.EXACTLY, operand: snapshotURL || '' }, not: false }],
    urlsLogicOperator: LogicOptions.ANY,
    showTimeframe: ModalTimeFrames.FOREVER,
  };
};

interface NewPlacementProps {
  createPlacement: (payload: Create) => EndpointActionResponse;
  contentId: string;
  newPlacement: Record<string, unknown> & { id: string };
  hideNotification?: boolean;
}

export const saveNewPlacement = async ({
  createPlacement,
  contentId,
  newPlacement,
  hideNotification,
}: NewPlacementProps) => {
  await createPlacement({
    body: newPlacement,
    invalidates: {
      [ENDPOINTS.contentPlacements]: {
        query: { finder: 'byContentId', contentId },
      },
    },
    onSuccessNotification: hideNotification ? null : undefined,
  });
};

interface UpdatePlacementProps {
  updatePlacement: (payload: Update) => EndpointActionResponse;
  contentId: string;
  updatedPlacement: Record<string, unknown> & { id: string };
  hideNotification?: boolean;
}

export const updateExistingPlacement = async ({
  updatePlacement,
  contentId,
  updatedPlacement,
  hideNotification,
}: UpdatePlacementProps) => {
  await updatePlacement({
    id: updatedPlacement.id,
    body: updatedPlacement,
    invalidates: {
      [ENDPOINTS.contentPlacements]: {
        query: { finder: 'byContentId', contentId },
      },
    },
    onSuccessNotification: hideNotification ? null : undefined,
  });
};

interface RemoveSnapshotWithPlacementProps {
  deletePlacement: undefined | ((payload: Delete) => EndpointActionResponse);
  placement: ContentPlacement;
  deleteSnapshot: (payload: Delete) => EndpointActionResponse;
  snapshotId: string;
  contentId: string;
  hideNotification?: boolean;
}

interface RemoveSnapshotProps
  extends Omit<RemoveSnapshotWithPlacementProps, 'deletePlacement' | 'placement'> {
  deletePlacement?: undefined | ((payload: Delete) => EndpointActionResponse);
  placement?: ContentPlacement;
}

export const removeSnapshot = async ({
  deleteSnapshot,
  deletePlacement,
  snapshotId,
  placement,
  contentId,
  hideNotification,
}: RemoveSnapshotProps | RemoveSnapshotWithPlacementProps) => {
  await deleteSnapshot({
    id: snapshotId,
    invalidates: {
      [ENDPOINTS.snapshots]: {
        query: { finder: 'byContentId', id: contentId },
      },
    },
    onSuccessNotification: hideNotification ? null : { message: 'Successfully Removed Snapshot' },
  });

  if (placement && deletePlacement) {
    await deletePlacement({
      id: placement.id,
      invalidates: {
        [ENDPOINTS.contentPlacements]: {
          query: { finder: 'byContentId', contentId },
        },
      },
      onSuccessNotification: null,
    });
  }
};

interface RetakeSnapshotProps {
  applicationId: number;
  contentId: string;
  targetUrl: string;
  removeSnapshotId?: string;
  placement?: ContentPlacement;
}

export const retakeSnapshot = ({
  applicationId,
  contentId,
  targetUrl,
  removeSnapshotId,
  placement,
}: RetakeSnapshotProps) => {
  const url = targetUrl.match(/^https?:/) ? targetUrl : `//${targetUrl}`;

  setForceSnapshotCookie({
    applicationId,
    contentId,
    url,
    removeSnapshotId,
    placement,
  });

  window.location.replace(url);
};

// Return true if snapshot has all viewport documents attached
export const checkSnapshotHasDocuments = (snapshot: Snapshot) => {
  const requiredKeys = ['smS3Url', 'mdS3Url', 'lgS3Url'];
  return requiredKeys.every((key) => key in snapshot);
};

interface CleanUpProps {
  contentType: ContentType;
  placements: ContentPlacement[] | undefined;
  snapshots: Snapshot[];
  contentId: ContentId;
  deleteSnapshot: (payload: Delete) => EndpointActionResponse;
  excludeSnapshotId?: string;
}

export const cleanUpDisconnectedSnapshots = async ({
  contentType,
  placements = [],
  snapshots,
  contentId,
  deleteSnapshot,
  excludeSnapshotId,
}: CleanUpProps) => {
  // For Inline, remove all snapshots that are not tied to a placement
  // For all other content, remove all snapshots except the latest one
  if (contentType === ContentType.Inline) {
    const placementSnapshots = placements.map((p) => p.snapshotId).filter((sId) => !!sId);
    await Promise.all(
      snapshots.map(async (snapshot) => {
        if (!placementSnapshots.includes(snapshot.id)) {
          await removeSnapshot({
            deleteSnapshot,
            snapshotId: snapshot.id,
            contentId,
            hideNotification: true,
          });
        }
      }),
    );
  } else if (snapshots.length > 1) {
    const latestSnapshotId = getLatestSnapshot(
      isTooltipContent(contentType)
        ? snapshots
        : snapshots.filter((s) => s.id !== excludeSnapshotId),
    )?.id;

    if (latestSnapshotId) {
      await Promise.all(
        snapshots.map(async (snapshot) => {
          if (snapshot.id !== latestSnapshotId) {
            await removeSnapshot({
              deleteSnapshot,
              snapshotId: snapshot.id,
              contentId,
              hideNotification: true,
            });
          }
        }),
      );
    }
  }
};

export const checkInvalidSelector = (selector?: string) => {
  let invalidSelector: boolean;
  // test selector for validity
  try {
    document.querySelector(selector ?? '');

    invalidSelector = !!selector?.includes('nth-child');
  } catch {
    invalidSelector = !!selector;
  }

  const querySelector = getSelectorFromIframe(selector);

  return {
    invalidSelector,
    querySelector,
  };
};

export const getPlacementUrlText = (placement: ContentPlacement) => {
  let placementUrl: string;
  if (placement.urlsLogicOperator === LogicOptions.ALL && !placement.urls) {
    placementUrl = PAGE_OPTIONS_TXT[PageOptions.EVERYWHERE];
  } else if (
    placement.urls?.length === 1 &&
    placement.urls[0].operation.type === URLOptions.EXACTLY
  ) {
    placementUrl = placement.urls?.[0].operation.operand ?? '';
  } else {
    placementUrl = 'Custom rule';
  }

  return placementUrl;
};
